import { useContext, useEffect, useRef } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';
import { isNavBypassing } from './navigationBypass';

// In-app unsaved-changes guard. react-router's useBlocker needs a data router,
// but the app mounts <BrowserRouter>, so we intercept navigation ourselves while
// `when` is true. Each attempt is handed to onNavigate(proceed): the page can
// confirm with the user and call proceed() to continue, or do nothing to stay.
// Covers in-app navigation (header back button, sidebar, any navigate()) and the
// browser back/forward button (popstate). F5 / tab close stays on the native
// beforeunload guard - the only thing browsers allow on a real unload.
export default function useNavigationGuard(when, onNavigate) {
    const { navigator } = useContext(UNSAFE_NavigationContext);
    const ref = useRef({ when, onNavigate });
    ref.current = { when, onNavigate };
    // Set when the user confirms leaving, so the popstate cleanup below does not
    // history.back() and bounce us back onto the page we just navigated away from.
    const leaving = useRef(false);

    // (1) In-app router navigation: patch the shared history navigator.
    useEffect(() => {
        const { push, replace } = navigator;
        const intercept =
            (original) =>
            (...args) => {
                // A confirmed action navigating under the bypass (e.g. logout
                // after the prompt) is a real leave: flag it so effect (2)'s
                // cleanup doesn't history.back() and bounce the URL back onto the
                // page we just left.
                if (isNavBypassing()) {
                    leaving.current = true;
                    return original.apply(navigator, args);
                }
                if (!ref.current.when) return original.apply(navigator, args);
                return ref.current.onNavigate(() => {
                    leaving.current = true;
                    original.apply(navigator, args);
                });
            };
        navigator.push = intercept(push);
        navigator.replace = intercept(replace);
        return () => {
            navigator.push = push;
            navigator.replace = replace;
        };
    }, [navigator]);

    // (2) Browser back/forward button: keep a same-URL sentinel entry on top so a
    // Back press is absorbed (the URL doesn't actually change, so react-router
    // stays put) and we can prompt instead. On confirm, pop the sentinel and the
    // page entry to honour the original Back.
    useEffect(() => {
        if (!when) return undefined;
        window.history.pushState(null, '', window.location.href);

        const onPopState = () => {
            window.history.pushState(null, '', window.location.href);
            ref.current.onNavigate(() => {
                leaving.current = true;
                window.removeEventListener('popstate', onPopState);
                window.history.go(-2);
            });
        };

        window.addEventListener('popstate', onPopState);
        return () => {
            window.removeEventListener('popstate', onPopState);
            // Drop the lingering sentinel when staying put (save / discard); when
            // leaving, the navigation already moved us, so a back() would undo it.
            if (!leaving.current) window.history.back();
        };
    }, [when]);
}
